import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CompanyHistory = () => {
  const milestones = [
    {
      year: "2012",
      title: "Company Founded",
      description: "Endosurgery Sdn Bhd established with a vision to revolutionize medical equipment distribution in Malaysia.",
      icon: "Rocket",
      color: "primary"
    },
    {
      year: "2015",
      title: "GDPMD Certification",
      description: "Achieved Good Distribution Practice for Medical Devices certification, ensuring highest quality standards.",
      icon: "Shield",
      color: "success"
    },
    {
      year: "2018",
      title: "Strategic Partnerships",
      description: "Formed exclusive partnerships with leading global manufacturers like Olympus and Pentax Medical.",
      icon: "Handshake",
      color: "accent"
    },
    {
      year: "2020",
      title: "Digital Transformation",
      description: "Launched comprehensive digital platform for seamless product ordering and customer support.",
      icon: "Monitor",
      color: "primary"
    },
    {
      year: "2022",
      title: "Market Leadership",
      description: "Became Malaysia's leading distributor of endoscopic equipment with 40% market share.",
      icon: "TrendingUp",
      color: "success"
    },
    {
      year: "2024",
      title: "Innovation Hub",
      description: "Opened state-of-the-art training facility for continuous medical education and product demonstrations.",
      icon: "GraduationCap",
      color: "accent"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Clock" size={16} color="var(--color-primary)" />
            <span className="text-primary font-semibold text-sm">Our Journey</span>
          </div>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-text-primary mb-6">
            12 Years of Excellence
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            From a small startup to Malaysia's leading medical equipment distributor, 
            our journey has been marked by innovation, dedication, and unwavering commitment to healthcare excellence.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 lg:left-1/2 lg:transform lg:-translate-x-0.5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-success"></div>

          <div className="space-y-12 lg:space-y-16">
            {milestones?.map((milestone, index) => (
              <motion.div
                key={milestone?.year}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Timeline Node */}
                <div className="absolute left-4 lg:left-1/2 lg:transform lg:-translate-x-1/2 flex items-center justify-center w-8 h-8 bg-card border-4 border-primary rounded-full shadow-medical z-10">
                  <div className={`w-3 h-3 bg-${milestone?.color} rounded-full`}></div>
                </div>

                {/* Content Card */}
                <div className={`ml-16 lg:ml-0 lg:w-5/12 ${
                  index % 2 === 0 ? 'lg:mr-auto lg:pr-8' : 'lg:ml-auto lg:pl-8'
                }`}>
                  <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-medical hover:shadow-medical-hover transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className={`flex items-center justify-center w-12 h-12 bg-${milestone?.color}/10 rounded-xl flex-shrink-0`}>
                        <Icon name={milestone?.icon} size={24} color={`var(--color-${milestone?.color})`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className={`text-2xl font-bold text-${milestone?.color}`}>
                            {milestone?.year}
                          </span>
                          <div className={`h-px flex-1 bg-${milestone?.color}/20`}></div>
                        </div>
                        <h3 className="text-xl font-semibold text-text-primary mb-3">
                          {milestone?.title}
                        </h3>
                        <p className="text-text-secondary leading-relaxed">
                          {milestone?.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16 lg:mt-20"
        >
          <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-success/5 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Ready to Shape the Future Together?
            </h3>
            <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
              Join us in our mission to advance Malaysian healthcare through innovative medical technology solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="MessageCircle" size={20} className="mr-2" />
                Contact Us
              </Link>
              <Link
                to="/product-portfolio"
                className="inline-flex items-center justify-center px-6 py-3 bg-transparent border border-primary text-primary font-semibold rounded-lg hover:bg-primary/5 transition-colors duration-200"
              >
                <Icon name="Package" size={20} className="mr-2" />
                View Products
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompanyHistory;